import { ref, computed } from 'vue';
import { useAuthStore } from '../stores/auth.ts';

interface Company {
  id: number;
  name: string;
  message: string;
  logo_url: string | null;
}

// Shared company data that can be used across components
const companyData = ref<Company | null>(null);
const isLoading = ref<boolean>(false);

export function useCompanyData() {
  const authStore = useAuthStore();
  const user = computed(() => authStore.user);

  /**
   * Fetch company data
   */
  const fetchCompanyData = async (): Promise<Company | null> => {
    try {
      isLoading.value = true;
      const companyId = user.value?.company_id;
      
      if (!companyId) {
        companyData.value = null;
        return null;
      }

      const response = await window.axios.get(`/api/companies/${companyId}`);
      companyData.value = response.data.company;
      return companyData.value;
    } catch (error) {
      console.warn('Could not fetch company data:', error);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update company data after successful save
   */
  const updateCompanyData = (newData: Company) => {
    companyData.value = newData;
  };

  /**
   * Get current company data
   */
  const getCompanyData = () => {
    return companyData.value;
  };

  return {
    companyData: computed(() => companyData.value),
    isLoading: computed(() => isLoading.value),
    fetchCompanyData,
    updateCompanyData,
    getCompanyData
  };
}